import { ListChecks, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  useCreate,
  useDelete,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

export interface ChecklistItem {
  id: string;
  task_id: number;
  position: number;
  label: string;
  done: boolean;
  done_at: string | null;
}

export interface ChecklistTemplate {
  id: string;
  name: string;
  items: string[];
}

/**
 * The checklist on a task. Ticking an item stamps when it happened
 * server-side, so a finished checklist doubles as a record of the work, * which is the point of having one on a job in the first place.
 */
export const TaskChecklist = ({
  taskId,
  className,
}: {
  taskId: number | string;
  className?: string;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [deleteOne] = useDelete();
  const [label, setLabel] = useState("");
  const [applying, setApplying] = useState(false);

  const { data: items } = useGetList<ChecklistItem>("task_checklist_items", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "position", order: "ASC" },
    filter: { task_id: taskId },
  });

  const { data: templates } = useGetList<ChecklistTemplate>(
    "checklist_templates",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "name", order: "ASC" },
    },
  );

  const done = (items ?? []).filter((item) => item.done).length;
  const total = (items ?? []).length;

  const addItem = () => {
    if (!label.trim()) return;
    create(
      "task_checklist_items",
      {
        data: {
          task_id: Number(taskId),
          label: label.trim(),
          position: total,
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          setLabel("");
          refresh();
        },
        onError: (error: unknown) =>
          notify(error instanceof Error ? error.message : "Could not add", {
            type: "error",
          }),
      },
    );
  };

  const toggle = (item: ChecklistItem) => {
    update(
      "task_checklist_items",
      { id: item.id, data: { done: !item.done }, previousData: item },
      { mutationMode: "pessimistic", onSuccess: () => refresh() },
    );
  };

  const applyTemplate = async (templateId: string) => {
    if (!templateId) return;
    setApplying(true);
    const { data, error } = await getSupabaseClient().rpc(
      "apply_checklist_template",
      { p_task_id: Number(taskId), p_template_id: templateId },
    );
    setApplying(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify(`Added ${data as number} checklist item(s)`);
    refresh();
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-foreground">
          <ListChecks className="size-3.5 text-muted-foreground" />
          Checklist
          {total > 0 ? (
            <span className="font-normal text-muted-foreground">
              {done}/{total}
            </span>
          ) : null}
        </p>
        {(templates ?? []).length > 0 ? (
          <select
            className={cn(inputClass, "h-8 w-auto max-w-[200px]")}
            value=""
            disabled={applying}
            onChange={(e) => applyTemplate(e.target.value)}
          >
            <option value="">Apply a template…</option>
            {(templates ?? []).map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {total > 0 ? (
        <div className="flex flex-col">
          {(items ?? []).map((item) => (
            <label
              key={item.id}
              className="group flex items-center gap-2.5 border-b border-border-subtle py-2 text-[13px] last:border-0"
            >
              <input
                type="checkbox"
                className="size-4 shrink-0 accent-primary"
                checked={item.done}
                onChange={() => toggle(item)}
              />
              <span
                className={cn(
                  "min-w-0 flex-1 break-words",
                  item.done && "text-muted-foreground line-through",
                )}
              >
                {item.label}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() =>
                  deleteOne(
                    "task_checklist_items",
                    { id: item.id, previousData: item },
                    { mutationMode: "pessimistic", onSuccess: () => refresh() },
                  )
                }
              >
                <Trash2 className="size-3.5" />
              </Button>
            </label>
          ))}
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <input
          className={inputClass}
          placeholder="Add a step…"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addItem();
            }
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="shrink-0"
          onClick={addItem}
        >
          <Plus className="size-3.5" />
        </Button>
      </div>
    </div>
  );
};
